import { AfterViewInit, Component, effect, input, Input, OnInit, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { NgSelectComponent } from '@ng-select/ng-select';
import { ToastrService } from '@shared/services/toastr.service';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ACL_ORGANIZATION_MANAGE, ACL_ORGANIZATION_OWNER_MANAGE, AuthService } from '@shared/services/auth.service';
import { copyObject } from '@shared/utils/data-utils';
import { isValidEmail } from '@shared/validators/validators';
import { Country, CountryService } from '@shared/services/country.service';
import { OrganizationUserService } from '@shared/services/organization-user.service';
import { HelpComponent } from '@shared/components/help/help.component';
import { NgFor, NgIf } from '@angular/common';
import { LabelComponent } from '@shared/components/label/label.component';
import { BadgeOrganizationComponent } from '@shared/components/badge-organization/badge-organization.component';
import { FormsModule } from '@angular/forms';
import { SwitchComponent } from '@shared/components/switch/switch.component';
import { FeatureFlagDirective } from '@shared/utils/feature-flag.directive';
import _ from 'lodash';
import { OrganizationInfo, OrganizationService } from '@shared/services/organization.service';

@Component({
  selector: 'app-organization',
  templateUrl: './organization.component.html',
  styleUrls: ['./organization.component.scss'],
  imports: [
    HelpComponent,
    NgIf,
    LabelComponent,
    BadgeOrganizationComponent,
    FormsModule,
    NgSelectComponent,
    NgFor,
    TranslatePipe,
    SwitchComponent,
    FeatureFlagDirective,
  ],
})
export class OrganizationComponent implements OnInit, AfterViewInit {
  organizationCode =  input.required<string>();

  @ViewChild('searchUsers') searchUsers: NgSelectComponent;

  editField = false;
  countries: Country[];
  users = [];
  organization: OrganizationInfo;
  previousOrganization: OrganizationInfo;

  ACL_ORGANIZATION_MANAGE = ACL_ORGANIZATION_MANAGE;
  ACL_ORGANIZATION_OWNER_MANAGE = ACL_ORGANIZATION_OWNER_MANAGE;

  countUsersWithApiKeyAndMfaNone = 0;

  protected readonly userFeatures = new Set(this.auth.user.features);

  constructor(
    public auth: AuthService,
    private readonly organizationService: OrganizationService,
    public translate: TranslateService,
    private toastr: ToastrService,
    private countryService: CountryService,
    private organizationUserService: OrganizationUserService,
  ) {
    effect(() => {
      this.organizationService.getOrganizationDetails(this.organizationCode()).subscribe(organizationDetails => {
        this.organization = _.pick(organizationDetails, ['name', 'owner', 'requireMfa', 'city', 'street', 'zipCode', 'countryCode']);
        this.savePreviousState();
      })
    });
  }

  async ngOnInit() {
    // TODO: use a resolver
    await this.countryService.init();
    this.countries = this.countryService.getCountries();
    if (this.userFeatures.has(ACL_ORGANIZATION_MANAGE)) {
      this.organizationUserService.countUsersWithApiKeyAndMfaNone().subscribe(
        (res) => (this.countUsersWithApiKeyAndMfaNone = res.value));
    }
  }

  ngAfterViewInit(): void {
    if (this.searchUsers) {
      fromEvent(this.searchUsers.element, 'keyup')
        .pipe(debounceTime(300))
        .subscribe((evt: any) => {
          const value = evt.target.value;
          if (value.length >= 3) {
            this.loadUsers(value);
          }
        });
    }
  }

  updateOrganization() {
    if (!isValidEmail(this.organization.owner.email)) {
      return this.toastr.error('InvalidEmail');
    }

    const { owner, ...rest } = this.organization;

    this.organizationService.updateOrganization(this.organizationCode(), { ...rest, ownerEmail: owner.email })
      .subscribe({
        next: () => {
          if (
            (this.userFeatures.has(ACL_ORGANIZATION_OWNER_MANAGE) && owner.email != this.auth.user.email) ||
            this.previousOrganization.requireMfa != this.organization.requireMfa
          ) {
            // change organization owner, so full update organization
            this.auth.init();
          } else {
            console.log('update currentOrganization', this.organization);
            this.savePreviousState();
          }

          this.previousOrganization = copyObject(this.organization);
          this.toastr.success();
          this.editField = false;
        },
        error: (error) => {
          this.toastr.error(error.error);
        },
      });
  }

  private savePreviousState() {
    this.auth.updateCurrentOrganizationInfo(this.organization);
  }

  onEditField = () => {
    this.editField = true;
  };

  loadUsers(searchTerm: string) {
    this.organizationUserService
      .getUsers({ filter: searchTerm }, { number: 0, size: 20 })
      .subscribe((page) => (this.users = page.content.map((item) => item.user)));
  }
}
